'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { Image, Typography, Video } from '../../primitives';
import { IconCta, RichText } from '../../composables';
import { MediaSource, CMSMediaData, AnalyticsProps } from '../../types';
import { createMediaSource, isHtmlContent } from '../../utils';
import { classNames } from '../../utils';
import styles from './MediaWrapper.module.scss';
import { YouTubeModal } from '../Modal/YouTubeModal/YouTubeModal';
import { Html5VideoModal } from '../Modal/Html5VideoModal/Html5VideoModal';

function extractCaptionContent(html: string): string {
  return html.replace(/^\s*<figcaption[^>]*>([\s\S]*)<\/figcaption>\s*$/i, '$1').trim();
}

interface MediaWrapperProps {
  media: MediaSource | CMSMediaData;
  className?: string;
  children?: React.ReactNode;
  priority?: boolean;
  borderRadius?: 'round' | 'square' | 'circle';
  autoplay?: 'autoplay' | 'controls' | undefined;
  /** Forces ButterCMS videos to display in embedded style (poster + modal) instead of inline video player */
  forceEmbedStyle?: boolean;
  /** Analytics configuration for the media wrapper and its interactive elements */
  analyticsConfig?: AnalyticsProps;
  /** Optional video metadata for richer tracking */
  videoMetadata?: {
    /** Explicit video title (takes priority over alt text) */
    title?: string;
    /** Explicit video ID (takes priority over URL extraction) */
    id?: string;
    /** Video duration in seconds (if known) */
    duration?: number;
  };
  /** Custom sizes attribute for responsive images */
  sizes?: string;
}

export const MediaWrapper: React.FC<MediaWrapperProps> = ({
  media,
  className,
  children,
  priority = false,
  borderRadius = 'round',
  autoplay = undefined,
  forceEmbedStyle = false,
  analyticsConfig,
  videoMetadata,
  sizes,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const imageSizes = sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1120px';
  const radiusClass =
    borderRadius !== 'square' ? styles[`media-wrapper--radius-${borderRadius}`] : '';

  // Convert CMS data to MediaSource if needed, with embed style configuration
  const mediaSource = useMemo(() => {
    if (media && 'type' in media) {
      return media; // Already a MediaSource
    }

    return createMediaSource(media, forceEmbedStyle);
  }, [media, forceEmbedStyle]);

  // Determine video provider for modals
  const videoProvider = useMemo(() => {
    if (!mediaSource) return 'unknown';
    if (mediaSource.type === 'embed') return 'youtube';
    if (mediaSource.type === 'embedCmsVideo') return 'cms-cdn';
    return 'unknown';
  }, [mediaSource]);

  const getEnhancedYoutubeUrl = useCallback(
    (originalUrl: string, shouldAutoplay: boolean = false) => {
      if (!originalUrl) return '';

      const url = new URL(originalUrl);
      // Add autoplay and enable JS API for better control
      if (shouldAutoplay) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('playsinline', '1'); // Better mobile support
      }
      url.searchParams.set('enablejsapi', '1');
      url.searchParams.set('rel', '0'); // Don't show related videos
      url.searchParams.set('modestbranding', '1'); // Reduce YouTube branding
      url.searchParams.set('playsinline', '1'); // Better mobile support

      return url.toString();
    },
    []
  );

  const getCmsEmbeddedUrl = useCallback((originalUrl: string, shouldAutoplay: boolean = false) => {
    if (!originalUrl) return '';

    const url = new URL(originalUrl);
    // Add Roblox-specific parameters
    if (shouldAutoplay) {
      url.searchParams.set('autoplay', '1');
    }
    url.searchParams.set('modestbranding', '1');
    url.searchParams.set('iv_load_policy', '3');
    url.searchParams.set('fs', '0');
    url.searchParams.set('rel', '0');

    return url.toString();
  }, []);

  const getYouTubeFallbackPoster = useCallback((embedUrl: string): string | null => {
    try {
      const url = new URL(embedUrl);
      const id = url.searchParams.get('v');
      if (!id) return null;
      return `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
    } catch {
      return null;
    }
  }, []);

  const handlePlayClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const renderMedia = () => {
    if (!mediaSource) {
      return null;
    }

    switch (mediaSource.type) {
      case 'image': {
        const image = (
          <Image
            src={mediaSource.src}
            alt={mediaSource.alt || ''}
            lazyLoading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'low'}
            sizes={imageSizes}
          />
        );
        if (!mediaSource.caption) return image;
        return (
          <figure className={styles['media-wrapper__figure']}>
            {image}
            <Typography
              variant="caption"
              as="figcaption"
              className={styles['media-wrapper__caption']}
            >
              {isHtmlContent(mediaSource.caption) ? (
                <RichText html={extractCaptionContent(mediaSource.caption)} />
              ) : (
                mediaSource.caption
              )}
            </Typography>
          </figure>
        );
      }

      case 'video':
        return (
          <Video
            media={mediaSource}
            mode={autoplay}
            preload={priority ? 'metadata' : 'none'}
            fetchPriority={priority ? 'high' : undefined}
            lazyLoad={!priority}
          />
        );

      case 'embed': {
        const fallbackPoster =
          !mediaSource.poster || mediaSource.poster.trim() === ''
            ? getYouTubeFallbackPoster(mediaSource.embedUrl)
            : null;
        const posterSrc = mediaSource.poster?.trim() || fallbackPoster;

        return (
          <>
            <div className={styles['media-wrapper__embed-container']}>
              {posterSrc ? (
                <Image
                  src={posterSrc}
                  alt={mediaSource.alt || ''}
                  lazyLoading={priority ? 'eager' : 'lazy'}
                  fetchPriority={priority ? 'high' : 'low'}
                  sizes={imageSizes}
                />
              ) : (
                <div className={styles['media-wrapper__embed-placeholder']} />
              )}
              <IconCta
                icon="play"
                ariaLabel={`${mediaSource.alt ? mediaSource.alt + ' - ' : ''}click to play video`}
                className={styles['media-wrapper__icon-cta-play']}
                onClick={handlePlayClick}
                size={'extra-large'}
              />
              {mediaSource.caption && (
                <figcaption className={classNames('caption', styles['media-wrapper__caption'])}>
                  {isHtmlContent(mediaSource.caption) ? (
                    <RichText html={extractCaptionContent(mediaSource.caption)} />
                  ) : (
                    mediaSource.caption
                  )}
                </figcaption>
              )}
            </div>
          </>
        );
      }

      case 'embedCmsVideo':
        return (
          <figure className={styles['media-wrapper__figure']}>
            <div className={styles['media-wrapper__embed-container']}>
              {mediaSource.poster && mediaSource.poster.trim() !== '' ? (
                <Image
                  src={mediaSource.poster}
                  alt={mediaSource.alt || ''}
                  lazyLoading={priority ? 'eager' : 'lazy'}
                  fetchPriority={priority ? 'high' : 'low'}
                  sizes={imageSizes}
                />
              ) : (
                <div className={styles['media-wrapper__embed-placeholder']} />
              )}
              <IconCta
                icon="play"
                ariaLabel={`${mediaSource.alt ? mediaSource.alt + ' - ' : ''}click to play video`}
                className={styles['media-wrapper__icon-cta-play']}
                onClick={handlePlayClick}
                size={'extra-large'}
              />
            </div>
            {mediaSource.caption && (
              <figcaption className={classNames('caption', styles['media-wrapper__caption'])}>
                {isHtmlContent(mediaSource.caption) ? (
                  <RichText html={extractCaptionContent(mediaSource.caption)} />
                ) : (
                  mediaSource.caption
                )}
              </figcaption>
            )}
          </figure>
        );

      default:
        return null;
    }
  };

  const videoSrc = useMemo(() => {
    if (!mediaSource) return '';
    if (mediaSource.type === 'embed') {
      return getEnhancedYoutubeUrl(mediaSource.embedUrl, true);
    }
    if (mediaSource.type === 'embedCmsVideo') {
      return getCmsEmbeddedUrl(mediaSource.videoUrl, true);
    }
    return '';
  }, [mediaSource, getEnhancedYoutubeUrl, getCmsEmbeddedUrl]);

  if (!mediaSource) {
    return null;
  }

  return (
    <>
      <div className={classNames('media-wrapper', className, radiusClass)}>
        {renderMedia()}
        {children}
      </div>

      {mediaSource && videoProvider === 'youtube' && mediaSource.type === 'embed' ? (
        <YouTubeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          src={videoSrc}
          mediaSource={mediaSource}
          videoMetadata={videoMetadata}
          analyticsConfig={analyticsConfig}
        />
      ) : null}

      {mediaSource && videoProvider === 'cms-cdn' && mediaSource.type === 'embedCmsVideo' ? (
        <Html5VideoModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          src={videoSrc}
          mediaSource={mediaSource}
          videoMetadata={videoMetadata}
          analyticsConfig={analyticsConfig}
        />
      ) : null}
    </>
  );
};
